import React, { useEffect, useState, useMemo } from "react";
import Fuse from "fuse.js";
import {
  useGetCtraderSymbol,
  useGetInstrumentSuggestion,
  useGetLeverateSymbol,
  useGetMatchSymbol,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { useUserContext } from "@/contexts/userContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { ChevronDownIcon, LucideLoader2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface SymbolSelectorProps {
  broker: string;
  selectedSymbols: string[];
  onSelected: (symbols: string[]) => void;
  isSingle?: boolean;
  disabled?: boolean;
}

const SymbolSelector: React.FC<SymbolSelectorProps> = ({
  broker,
  selectedSymbols,
  onSelected,
  isSingle = false,
  disabled = false,
}) => {
  const { user, connectedBrokers } = useUserContext();

  const [instrumentInput, setInstrumentInput] = useState("");
  const [trendingSymbolData, setTrendingSymbolData] = useState<any[]>([]);
  const [symbolData, setSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);

  const [showSuggestion, setShowSuggestion] = useState(false);
  const [currentSymbolData, setcurrentSymbolData] = useState<any[]>([]);

  const { data: instrumentSuggestionData, isLoading: isInstrumentLoading } =
    useGetInstrumentSuggestion(user?.user_id, undefined, broker);

  const { data: matchSymbolsData } = useGetMatchSymbol("forex");

  const { data: cTraderSymbolData, error: cTraderError } = useGetCtraderSymbol(
    user?.user_id,
  );

  const { data: trendingSymbolsData } = useGetTrendingSymbols("equity", broker);

  const { data: leverateSymbolData } = useGetLeverateSymbol(
    user?.user_id,
    broker ?? "",
  );

  const brokerSymbolData: any = {
    "4": instrumentSuggestionData, // Alpaca
    "13": instrumentSuggestionData, // Lightspeed
    "1": matchSymbolsData, // Match-trade
    "2": cTraderSymbolData, // C-Trader
    "17": instrumentSuggestionData, // Fennel
    "5": instrumentSuggestionData, // Tradezero
    "11": leverateSymbolData, // Leverate
  };

  //Trending symbols effect
  useEffect(() => {
    if (["11"].includes(broker ?? "") && leverateSymbolData) {
      setTrendingSymbolData(
        leverateSymbolData.data.slice(0, 5).map((item: string) => ({
          short_name: "prop",
          symbol: item,
          name: item,
        })),
      );
    } else if (broker && trendingSymbolsData) {
      setTrendingSymbolData(
        Object.entries(trendingSymbolsData.data)
          .slice(0, 5)
          .map(([symbol, name]) => ({
            name: name as string,
            symbol,
            short_name: symbol,
          })),
      );
    }
  }, [broker, trendingSymbolsData, leverateSymbolData]);

  //Current symbols effect
  useEffect(() => {
    if (instrumentSuggestionData) {
      setcurrentSymbolData([...instrumentSuggestionData.data]);
    }
  }, [
    broker,
    trendingSymbolsData,
    matchSymbolsData,
    cTraderSymbolData,
    instrumentSuggestionData,
    leverateSymbolData,
  ]);

  const symbolList = instrumentInput ? currentSymbolData : trendingSymbolData;

  // ✅ Create Fuse instance once (memoized)
  const fuse = useMemo(() => {
    return new Fuse(symbolList, {
      keys: ["symbol", "name"], // searchable fields
      threshold: 0.3, // lower = stricter matching
      minMatchCharLength: 1,
      ignoreLocation: true,
    });
  }, [
    symbolList,
    currentSymbolData,
    trendingSymbolData,
    instrumentInput,
    broker,
    connectedBrokers,
  ]);

  // ✅ Perform search (with slice limit)
  const results = useMemo(() => {
    try {
      if (!instrumentInput?.trim() && symbolList)
        return symbolList?.slice(0, 10) || [];
      if (!fuse) return [];
      return fuse
        ?.search(instrumentInput)
        ?.slice(0, 10)
        .map((res) => res.item);
    } catch (error) {
      console.log("error", trendingSymbolData, currentSymbolData);
      return [];
    }
  }, [instrumentInput, fuse, symbolList, broker, connectedBrokers]);

  /** Handle input change + filtering */
  const handleSymbolInput = (value: string) => {
    setInstrumentInput(value);
  };

  /** --- Selecting symbol --- */
  const handleSelectSymbol = (sym: string) => {
    if (isSingle) {
      // Replace existing symbol
      onSelected([sym]);
      setInstrumentInput(sym);
      setShowSuggestion(false);
      return;
    }

    // Multi-select mode max=5
    if (selectedSymbols?.length >= 5) {
      toast({ title: "Maximum 5 symbols allowed", variant: "destructive" });
      setInstrumentInput("");
      return;
    }

    if (!selectedSymbols.includes(sym)) {
      onSelected([...selectedSymbols, sym]);
    }

    setInstrumentInput("");
    setShowSuggestion(false);
  };

  const handleRemoveSymbol = (symbol: string) => {
    onSelected(selectedSymbols.filter((s) => s !== symbol));
  };

  return (
    <div className="space-y-3">
      {/* Symbol Search Input */}
      <Popover open={showSuggestion} onOpenChange={setShowSuggestion}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            disabled={disabled}
            onClick={() => {
              setInstrumentInput("");
              setShowSuggestion(true);
            }}
            className="w-full justify-between text-[--accent-primary] border-[--accent-primary]"
          >
            {isSingle
              ? selectedSymbols[0] || "Select symbol"
              : instrumentInput || "Select symbol"}
            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[300px] overflow-auto">
          <Command filter={() => 1}>
            <CommandInput
              value={instrumentInput}
              onValueChange={handleSymbolInput}
              placeholder="Search symbol"
            />

            {isInstrumentLoading && (
              <div className="p-3 flex justify-center">
                <LucideLoader2 className="animate-spin" />
              </div>
            )}

            {!isInstrumentLoading && results.length === 0 && (
              <CommandEmpty>No symbols found.</CommandEmpty>
            )}

            <CommandGroup
              heading={!instrumentInput ? "Trending Symbols" : undefined}
            >
              {results.map((sym) => (
                <CommandItem
                  key={sym.symbol}
                  value={sym.symbol}
                  onSelect={() => handleSelectSymbol(sym.symbol)}
                >
                  <div className="flex flex-col">
                    <p>{sym.symbol}</p>
                    <p className="text-muted-foreground">{sym.name}</p>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Symbols */}
      {!isSingle && selectedSymbols.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedSymbols.map((sym) => (
            <span
              key={sym}
              className="bg-[--accent-primary] text-white px-3 py-1 rounded-full text-sm cursor-pointer hover:bg-[--accent-primary] transition"
              onClick={() => handleRemoveSymbol(sym)}
            >
              {sym} ✕
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default SymbolSelector;
